//--------------------------------------------------------------------//
//******************** wavRecorder *************************************//
//--------------------------------------------------------------------//
// Records raw PCM from a getUserMedia audio stream and hand-encodes a WAV
// Blob client-side. Plain JS, no Angular - per
// sip-gudhub-server/docs/sip-trunks/voice-mail-greeting-client-guide.md,
// MediaRecorder cannot produce WAV in any browser (WebM/Opus or MP4/AAC
// only, no mimeType option changes that), and the voicemail-greeting server
// route does no transcoding - it only accepts audio/wav.

// Hard safety cap on a single recording. At 16-bit mono the upload size is
// sampleRate * 2 bytes/sec, so 120s stays under the server's 15MB request
// limit even at a 48kHz native rate (~11.5MB) or 44.1kHz (~10.6MB).
export const MAX_RECORDING_SECONDS = 120;

// * stream - a getUserMedia({ audio: true }) MediaStream
// Returns { stop() } - stop() tears down the audio graph and returns the
// encoded WAV Blob. Caller is still responsible for stopping the stream's
// own tracks (this only disconnects/closes the AudioContext side).
export function recordWavBlob(stream) {
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    // ScriptProcessorNode is deprecated but universally supported and
    // simplest for this - an AudioWorkletNode is the modern replacement if
    // this ever needs to drop the deprecation warning, same underlying
    // approach (buffer PCM chunks, concatenate on stop).
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    const chunks = [];

    source.connect(processor);
    processor.connect(audioContext.destination);
    processor.onaudioprocess = (e) => chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));

    return {
        stop() {
            processor.disconnect();
            source.disconnect();
            processor.onaudioprocess = null;
            const sampleRate = audioContext.sampleRate; // native rate (commonly 48000) - no resampling needed, WAV header just records whatever it is
            audioContext.close();
            return encodeWav(chunks, sampleRate);
        }
    };
}

function encodeWav(chunks, sampleRate) {
    const length = chunks.reduce((n, c) => n + c.length, 0);
    const pcm16 = new Int16Array(length);
    let offset = 0;
    for (const chunk of chunks) {
        for (let i = 0; i < chunk.length; i++) {
            const s = Math.max(-1, Math.min(1, chunk[i]));
            pcm16[offset++] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
    }

    const buffer = new ArrayBuffer(44 + pcm16.length * 2);
    const view = new DataView(buffer);
    const writeStr = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };

    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + pcm16.length * 2, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);       // PCM fmt chunk size
    view.setUint16(20, 1, true);        // PCM format
    view.setUint16(22, 1, true);        // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); // byte rate (mono, 16-bit)
    view.setUint16(32, 2, true);        // block align
    view.setUint16(34, 16, true);       // bits per sample
    writeStr(36, 'data');
    view.setUint32(40, pcm16.length * 2, true);
    new Int16Array(buffer, 44).set(pcm16);

    return new Blob([buffer], { type: 'audio/wav' });
}
